import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, imgUrl } from "../lib/api";
import { ArrowLeft, X } from "lucide-react";

const JournalPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/journal/${slug}`)
      .then((r) => {
        if (!cancelled) setPost(r.data);
      })
      .catch(() => {
        if (!cancelled) setPost(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  if (loading) {
    return (
      <div className="px-6 py-32 text-center text-[#849383]" data-testid="journal-post-loading">
        Loading…
      </div>
    );
  }

  if (!post) {
    return (
      <div className="px-6 py-32 text-center" data-testid="journal-post-missing">
        <h1 className="font-serif-display text-4xl text-[#1A1F1B]">
          That entry isn't here.
        </h1>
        <Link to="/journal" className="btn-primary mt-8 inline-block">
          Back to journal
        </Link>
      </div>
    );
  }

  const paragraphs = (post.body || "").split(/\n{2,}/).filter(Boolean);

  return (
    <article className="px-6 md:px-12 lg:px-20 py-16 md:py-24" data-testid="page-journal-post">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/journal"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#849383] hover:text-[#3B4A3F] transition-colors"
          data-testid="journal-post-back"
        >
          <ArrowLeft size={14} /> Back to journal
        </Link>

        <div className="mt-8">
          <div className="text-xs uppercase tracking-[0.22em] text-[#849383]">
            {formatDate(post.created_at)}
          </div>
          <h1
            className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#1A1F1B] mt-4 leading-[1.05]"
            data-testid="journal-post-title"
          >
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="mt-6 text-lg text-[#4E5C50] italic leading-relaxed">
              {post.excerpt}
            </p>
          )}
        </div>

        <div className="mt-12 overflow-hidden border border-[#D8D3CC] bg-[#EFECE5]">
          <img
            src={imgUrl(post.cover_image_url)}
            alt={post.title}
            className="w-full aspect-[16/10] object-cover"
          />
        </div>

        <div
          className="mt-12 space-y-6 text-[#1A1F1B] leading-relaxed text-lg"
          data-testid="journal-post-body"
        >
          {paragraphs.map((p, i) => (
            <p key={i} className="whitespace-pre-wrap">
              {p}
            </p>
          ))}
        </div>

        {post.gallery_urls && post.gallery_urls.length > 0 && (
          <div className="mt-16">
            <div className="label-eyebrow mb-6">From the studio</div>
            <div
              className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4"
              data-testid="journal-post-gallery"
            >
              {post.gallery_urls.map((u, i) => (
                <button
                  type="button"
                  key={u}
                  onClick={() => setLightbox(u)}
                  className="overflow-hidden bg-[#EFECE5] border border-[#D8D3CC] art-card"
                  data-testid={`journal-post-gallery-${i}`}
                >
                  <img
                    src={imgUrl(u)}
                    alt={`Gallery ${i + 1}`}
                    loading="lazy"
                    className="w-full aspect-square object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-[#1A1F1B]/85 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
          data-testid="journal-post-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 p-2 text-[#F7F6F2] hover:bg-white/10"
            aria-label="Close"
          >
            <X size={24} />
          </button>
          <img
            src={imgUrl(lightbox)}
            alt="Gallery"
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </article>
  );
};

export default JournalPost;
